import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  Inbox,
  Loader2,
  Mail,
  Paperclip,
  Pencil,
  Save,
  Send,
  Trash2,
  Truck,
  UserRound,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PanelSkeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { quotationService } from '../../services/quotationService';
import { quotationIntakeService } from '@features/quotation-intake/services';
import type { BaseMarkup } from '@features/quotation-intake/types/quotationIntake.types';
import { QuotationComposeForm } from '../QuotationComposeForm';
import { QuotationReviewView } from '../review';
import { FullscreenReviewShell } from '@/components/FullscreenReviewShell';
import { QuotationStatusIcon } from '../QuotationStatusIcon';
import { CuttingListPostingCard } from '../CuttingListPostingCard';
import {
  fetchOutboxStatus,
  optimisticPostingJob,
  postingLocksEntity,
  postingBlocksSubmit,
  submitBlockedReason,
  outboxKey,
  outboxType,
  type OutboxPostingStatusDto,
} from '@shared/posting';
import { ConfirmDraftActionDialog } from '../ConfirmDraftActionDialog';
import { useQuotationDraftStore } from '../../store/quotationDraftStore';
import { useQuotationWorkspaceStore } from '../../store/quotationWorkspaceStore';
import { onQuotationEvent } from '../../hooks/useQuotationEventStream';
import { QuotationEventType } from '../../lib/quotationEventStream';
import { useGmailMailboxAddress } from '@features/shared/store/publicConfigStore';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { parseSenderName } from '../../utils/senderName';
import {
  EmailExtractionStatus,
  parseFreightCharge,
  parseFreightInputDraft,
  PickupType,
  type QuotationDraft,
  type QuotationListItemDto,
  QuotationStatus,
  serializeFreightInputDraft,
  WaivedFreight,
} from '../../types/quotation.types';
import { computePricingContext, formatPhp } from '../../utils/pricing';
import { FreightCalculatorPanel, type FreightPatch } from '@features/freight/components/FreightCalculatorPanel';
import { PricingDriftDialog } from './PricingDriftDialog';
import { CuttingListPostDialog } from './CuttingListPostDialog';
import { AutoSaveStatusChip } from '@features/shared/components/AutoSaveStatusChip';
import { PricingDriftError, type PricingDriftDetail } from '../../types/pricingDrift.types';

// Feature flag — hides the freight calculator from users while the feature is still in development.
// Backend column additions are additive (nullable) and the cutting-list freight-line append only
// fires when both FreightTotalAmount/DestinationId are populated, which the UI never does when
// this is false. Flip to false to re-disable.
const FREIGHT_FEATURE_ENABLED = true;
import { isEditableRow, isKgOnlyDraft, isPostedRow, KG_ONLY_NOTE } from '../../utils/lineItem';
import { isPostedQuotationStatus, shouldRefetchOnReactivate } from '../../utils/reactivate';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { formatDateTime12h, formatRelative } from '../../utils/formatRelative';


// CSC-291: header-sized variant of the extraction-status pill rendered on the
// master list. Uses the same icon language so the workspace stays consistent;
// returns null on NotApplicable so manual drafts get no badge.
function DetailExtractionStatusBadge({
  status,
  kgOnly = false,
}: {
  status: EmailExtractionStatus;
  kgOnly?: boolean;
}) {
  // 14ykpqtr22u — every extracted line was in kilograms: nothing to quote as pieces. A text badge
  // (not an icon-only glyph), with the full note on hover.
  if (kgOnly && status !== EmailExtractionStatus.Pending) {
    return (
      <span
        title={KG_ONLY_NOTE}
        className="inline-flex items-center rounded-sm border border-border px-1 text-xs font-medium text-muted-foreground"
      >
        Kg only
      </span>
    );
  }
  switch (status) {
    case EmailExtractionStatus.Pending:
      return (
        <span title="Extraction in progress" className="inline-flex items-center text-info">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        </span>
      );
    case EmailExtractionStatus.Completed:
      return (
        <span title="Extraction completed" className="inline-flex items-center text-success">
          <CheckCircle2 className="h-3.5 w-3.5" />
        </span>
      );
    case EmailExtractionStatus.SkippedAmbiguous:
      return (
        <span
          title="Extraction skipped — email was ambiguous"
          className="inline-flex items-center text-warning"
        >
          <AlertCircle className="h-3.5 w-3.5" />
        </span>
      );
    case EmailExtractionStatus.Failed:
      return (
        <span
          title="Extraction failed — open Review and Retry"
          className="inline-flex items-center text-destructive"
        >
          <XCircle className="h-3.5 w-3.5" />
        </span>
      );
    case EmailExtractionStatus.NotApplicable:
    default:
      return null;
  }
}

interface Props {
  selectedId: number | null;
  /** Server-side list-row DTO for the selected row, used to mount the
   *  AutoDraftReviewCard header without an extra round-trip. */
  selectedListItem: QuotationListItemDto | null;
  /** Called after every successful saveDraft. The workspace uses this to
   *  promote selectedId when an unsaved-local draft just got its serverId
   *  AND to bump the master list's refreshKey so the new/updated row
   *  appears without a manual reload. */
  onAfterSave?: (savedServerId: number) => void;
}

// 86d32t0xr — trailing-edge debounce window for auto-save. Tuned with the
// ticket: short enough that closing the tab risks at most ~2.5s of edits;
// long enough that fast typing in the panel doesn't burn the server with
// 30 saves a second. The debounce is reset on every dirty=true transition
// so a burst of keystrokes produces exactly one save fired at the trailing
// edge.
const AUTO_SAVE_DEBOUNCE_MS = 2500;

// Inline compose detail panel — the sole quotation edit/post surface (86d3k9589).
// Owns the draft-store orchestration (compose form, save semantics, submit flow)
// for the workspace; the legacy standalone /quotations/compose page was retired
// and now redirects here.

export function QuotationDetailPanel({ selectedId, selectedListItem, onAfterSave }: Props) {

  const drafts = useQuotationDraftStore((s) => s.drafts);
  const activeId = useQuotationDraftStore((s) => s.activeId);
  const loadDraft = useQuotationDraftStore((s) => s.loadDraft);
  const clearActive = useQuotationDraftStore((s) => s.clearActive);
  const update = useQuotationDraftStore((s) => s.update);
  const saveDraft = useQuotationDraftStore((s) => s.saveDraft);
  const markPosting = useQuotationDraftStore((s) => s.markPosting);
  const ackPosting = useQuotationDraftStore((s) => s.ackPosting);
  const isSaving = useQuotationDraftStore((s) => s.isSaving);
  const lastSavedAt = useQuotationDraftStore((s) => s.lastSavedAt);
  const saveError = useQuotationDraftStore((s) => s.saveError);
  // CSC-288: review mode lives in the shared draftStore (the legacy compose
  // page also reads/writes it). For the workspace, we surface it as an
  // explicit Review button on AutoDraft rows; the body region swaps the
  // compose form for QuotationReviewView when on.
  const reviewMode = useQuotationDraftStore((s) => s.reviewMode);
  const setReviewMode = useQuotationDraftStore((s) => s.setReviewMode);
  // CSC-289: per-user unread alert. Master list stamps this on first-open of an
  // unread row; we render a dismissible banner when it matches the active row.
  const unreadAlertQuotationId = useQuotationWorkspaceStore((s) => s.unreadAlertQuotationId);
  const setUnreadAlertQuotationId = useQuotationWorkspaceStore((s) => s.setUnreadAlertQuotationId);
  const mailboxAddress = useGmailMailboxAddress();
  const currentUserHasNsEmployee = useAuthStore((s) => Boolean(s.user?.netSuiteEmployeeId));
  const canArchive = useAuthStore((s) => s.hasPermission(PERMISSIONS.QUOTATIONS_ARCHIVE_QUOTATION));
  const canDelete = useAuthStore((s) => s.hasPermission(PERMISSIONS.QUOTATIONS_DELETE));
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [baseMarkup, setBaseMarkup] = useState<BaseMarkup | null>(null);
  // 86d3j5qca — `loading` lets the submit gate distinguish "config still loading" from
  // "config failed to load" (both leave baseMarkup null).
  const [baseMarkupLoading, setBaseMarkupLoading] = useState(true);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 86d3hm2vr — the "Create Cutting List" review modal. handleSubmit saves + stages the
  // draft/freight-note here and opens it; confirmPost (its onConfirm) runs the real POST.
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [pendingPost, setPendingPost] = useState<{ saved: QuotationDraft; freightPartial: boolean } | null>(
    null,
  );
  // 86d32t157 Phase 3 — when the submit returns 409 PRICING_DRIFT, the structured
  // detail goes here; the modal opens iff this is non-null. Refresh-and-resubmit
  // is wrapped in handleDriftRefresh below.
  const [pricingDriftDetail, setPricingDriftDetail] = useState<PricingDriftDetail | null>(null);

  // CSC-288 Phase 2: cabinet-door overlay for the customer panel. The form
  // renders its customer card via createPortal into `cabinetTargetEl` when
  // mode='portal'. The overlay div is always mounted so the portal target
  // is stable; we just toggle visibility via `cabinetOpen`. Click-outside
  // closes the cabinet (mousedown listener; checks both the trigger button
  // and the overlay content).
  const [cabinetOpen, setCabinetOpen] = useState(false);
  const [cabinetTargetEl, setCabinetTargetEl] = useState<HTMLDivElement | null>(null);
  const cabinetTriggerRef = useRef<HTMLButtonElement>(null);

  // Freight cabinet — mirrors the customer-details cabinet mechanics. The two
  // cabinets are mutually exclusive (opening one closes the other) since both
  // anchor to the top of the body region at z-40.
  const [freightCabinetOpen, setFreightCabinetOpen] = useState(false);
  const freightCabinetRef = useRef<HTMLDivElement | null>(null);
  const freightCabinetTriggerRef = useRef<HTMLButtonElement>(null);

  // Realtime stale-record notice. Set by the SSE listener below when a
  // collaborator submits/updates/deletes the row this panel is viewing.
  // Drives the blocking modal in the body region — null = no modal.
  const [staleNotice, setStaleNotice] = useState<
    { kind: 'updated'; status: QuotationStatus } | { kind: 'deleted' } | null
  >(null);
  const [isReloadingStale, setIsReloadingStale] = useState(false);

  // Avoid refetching the same row when selectedId hasn't actually changed
  // (effect re-runs from neighbouring deps would otherwise hit getById).
  const lastHydratedIdRef = useRef<number | null>(null);

  // Base-markup config — needed by the compose form header + the submit price gate.
  // 86d3j5qca: exposed as a callable so a failed load can be retried from the submit toast,
  // and a `loading` flag so the gate can tell "still loading" from "load failed".
  const loadBaseMarkup = useCallback(async () => {
    setBaseMarkupLoading(true);
    try {
      const m = await quotationIntakeService.getBaseMarkup();
      setBaseMarkup(m);
    } catch (err) {
      console.error('Failed to load base markup', err);
      setBaseMarkup(null);
    } finally {
      setBaseMarkupLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBaseMarkup();
  }, [loadBaseMarkup]);

  // Click-outside detection for the cabinet door. The cabinet content is
  // rendered via portal into cabinetTargetEl; the trigger is the header
  // button. A mousedown anywhere else closes the overlay. Listener is only
  // attached when the cabinet is open so we don't pay the cost on every
  // page click.
  //
  // Ignore Radix popper portals (Select dropdowns, comboboxes, popovers)
  // and dialog/listbox roles — they render into document.body and would
  // otherwise be detected as "outside" the cabinet, closing it the moment
  // the user picks an option from a customer-panel dropdown.
  useEffect(() => {
    if (!cabinetOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (cabinetTargetEl?.contains(target)) return;
      if (cabinetTriggerRef.current?.contains(target)) return;
      if (target.closest('[data-radix-popper-content-wrapper]')) return;
      if (target.closest('[role="dialog"], [role="listbox"], [role="menu"]')) return;
      setCabinetOpen(false);
    };
    window.addEventListener('mousedown', handleMouseDown);
    return () => window.removeEventListener('mousedown', handleMouseDown);
  }, [cabinetOpen, cabinetTargetEl]);

  // Same click-outside dance for the freight cabinet. Same Radix exclusions so
  // the location combobox popover doesn't close it.
  useEffect(() => {
    if (!freightCabinetOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (freightCabinetRef.current?.contains(target)) return;
      if (freightCabinetTriggerRef.current?.contains(target)) return;
      if (target.closest('[data-radix-popper-content-wrapper]')) return;
      if (target.closest('[role="dialog"], [role="listbox"], [role="menu"]')) return;
      setFreightCabinetOpen(false);
    };
    window.addEventListener('mousedown', handleMouseDown);
    return () => window.removeEventListener('mousedown', handleMouseDown);
  }, [freightCabinetOpen]);

  // Close the cabinet AND exit review mode when the user navigates away
  // from one row to another — the new selection's contents are different,
  // so reopening either UI should be an explicit user gesture.
  useEffect(() => {
    setCabinetOpen(false);
    setFreightCabinetOpen(false);
    setReviewMode(false);
  }, [selectedId, setReviewMode]);

  // selectedId -> hydrate draft into the shared store. Reuses an already-
  // open tab when present so save state and dirty flags survive navigation.
  useEffect(() => {
    if (!selectedId) {
      lastHydratedIdRef.current = null;
      return;
    }
    if (lastHydratedIdRef.current === selectedId) return;

    lastHydratedIdRef.current = selectedId;
    setLoadError(null);

    const state = useQuotationDraftStore.getState();
    const already = state.drafts.find((d) => d.serverId === selectedId);
    if (already) {
      state.activate(already.id);
      // 14ykpqtr2kq — the cached copy paints first (instant); then, if this session posted it, reconcile
      // in the background. The live-event handlers only listen for the ACTIVE quotation, so a post that
      // finished while another one was selected never reached this copy — it would still read Draft,
      // editable, although the quotation is Submitted.
      if (shouldRefetchOnReactivate(already, state.postingDraftIds)) {
        quotationService
          .getById(selectedId)
          .then((fresh) => {
            // Moved on already: loadDraft also sets activeId, so applying this would pull the panel back.
            if (lastHydratedIdRef.current !== selectedId) return;
            // Re-check on arrival: the terminal SSE event may have been applied meanwhile (acked, now
            // Submitted), or the user may have started editing — a late Draft response must not undo either.
            const now = useQuotationDraftStore.getState();
            const current = now.drafts.find((d) => d.serverId === selectedId);
            if (!current || !shouldRefetchOnReactivate(current, now.postingDraftIds)) return;
            // No `force` — keeps any local edits; a posted copy is always rebuilt from the server.
            loadDraft(fresh);
            // Still Draft = the post is in flight or failed: stay tracked so the active-draft SSE handler
            // below applies its terminal event in place, as it does today.
            if (isPostedQuotationStatus(fresh.status)) ackPosting(selectedId);
          })
          .catch(() => {
            // Background reconcile, like refreshAfterPosting: a reload recovers, and the server refuses
            // edits to a posted quotation with a clear message.
          });
      }
      return;
    }

    setIsLoadingDraft(true);
    quotationService
      .getById(selectedId)
      .then((hydrated) => {
        loadDraft(hydrated);
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : 'Failed to load quotation');
      })
      .finally(() => setIsLoadingDraft(false));
  }, [selectedId, loadDraft, ackPosting]);

  // Active draft resolution:
  //   - selectedId set    → use the draft whose serverId matches (hydrate
  //                         path: existing-row navigation).
  //   - selectedId null   → use the active draft regardless of serverId. The
  //                         "compose new" path (workspace `+` button) lands
  //                         here with a fresh blank; the auto-save first-save
  //                         transition also lands here for one render between
  //                         the save response and the parent's selectedId
  //                         promotion. The nav-away guard below clears
  //                         `activeId` on a real value→null transition, so
  //                         this branch doesn't surface a stale saved draft
  //                         after archive / delete / restore / URL back-nav.
  const activeDraft: QuotationDraft | null = drafts.find((d) => {
    if (d.id !== activeId) return false;
    if (selectedId === null) return true;
    return d.serverId === selectedId;
  }) ?? null;

  // Nav-away guard. Fires when selectedId TRANSITIONS from a real value to
  // null while the active draft already has a serverId — the signature of
  // archive / delete / restore (master list drops the row → setSelectedId(null))
  // and browser-back-nav to a URL without `?id=`. Clears `activeId` so the
  // panel falls to EmptyState instead of rendering the now-stale active draft.
  //
  // Deliberately gated on `prev !== null`: the auto-save first-save transition
  // sits at selectedId=null both before and after the save response lands
  // (selectedId only changes when onAfterSave promotes it to the new serverId),
  // so this effect doesn't fire on that path — the compose form stays mounted
  // and any open modal/state (e.g. bulk-edit) survives the save tick.
  const prevSelectedIdRef = useRef<number | null>(selectedId);
  useEffect(() => {
    const prev = prevSelectedIdRef.current;
    prevSelectedIdRef.current = selectedId;
    if (prev !== null && selectedId === null && activeDraft?.serverId) {
      clearActive();
    }
  }, [selectedId, activeDraft, clearActive]);

  // Realtime sync: when a collaborator submits/updates/deletes the row this
  // panel is currently viewing, raise a blocking modal in the body region
  // instead of silently overwriting the user's unsaved edits. The Reload
  // action refetches via getById and re-hydrates the draft store — no page
  // reload, no tab churn, other open drafts untouched. Self-echoes from the
  // viewer's own save are skipped because the local store has already been
  // updated to the new status by the time the SSE event arrives.
  // 86d3jkwuq — consume an OWN-submit terminal event: refresh the panel in place (no blocking
  // stale modal) so Posting → Submitted/PostingFailed transitions live, then stop tracking + toast.
  const refreshAfterPosting = useCallback(
    async (serverId: number) => {
      try {
        const fresh = await quotationService.getById(serverId);
        loadDraft(fresh, { force: true });
      } catch {
        // The master list still splices the row; a manual reload recovers the panel.
      }
      ackPosting(serverId);

      // ⛔ N8 (86d3w2kez): THE SETTLE-OUTCOME TOASTS THAT LIVED HERE ARE GONE — deliberately, and they
      // must not come back. The notification centre now raises exactly ONE toast per posting outcome,
      // via `notifyPostingOutcome`, keyed on the job so a retry storm updates one toast rather than
      // stacking. Left in place, this block would fire alongside it and every cutting-list post would
      // double-toast. The `postingStatus` argument went with them — this callback's real job, and the
      // reason it survives, is refreshing the panel in place.
      //
      // ✅ The imperative click-acks further down (cancel / reconcile) STAY. They fire on the user's
      // own click, at a different moment from settle, and they are what meets the sub-100ms feedback
      // gate. Only the settle-outcome toasts were removed.
    },
    [loadDraft, ackPosting],
  );

  useEffect(() => {
    const serverId = activeDraft?.serverId;
    if (!serverId) return;
    const localStatus = activeDraft.status;

    const off = onQuotationEvent((evt) => {
      if (evt.quotationId !== serverId) return;

      if (evt.eventType === QuotationEventType.Deleted) {
        setStaleNotice({ kind: 'deleted' });
        return;
      }

      if (evt.eventType !== QuotationEventType.Updated) return;

      // 86d3jkwuq — a terminal event for a draft THIS session claimed is our own posting result:
      // refresh the panel in place, no stale modal. Posting echoes are skipped (already flipped).
      // N8: the outcome TOAST is no longer raised here — the notification centre owns it, exactly
      // once, keyed on the job. This still refreshes so the card and status band move live.
      if (useQuotationDraftStore.getState().postingDraftIds.has(serverId)) {
        const ps = evt.postingStatus;
        if (ps === 'Submitted' || ps === 'Failed') {
          void refreshAfterPosting(serverId);
        }
        return;
      }

      // Same-status echoes (the viewer's own save, or a collaborator edit that doesn't change
      // status) are skipped. Status flips are the case worth alerting. 86d3mnuxe — a posting-only event
      // (null entity status) is handled above, so ignore it here.
      if (evt.status === null || evt.status === localStatus) return;

      setStaleNotice({ kind: 'updated', status: evt.status });
    });

    return () => {
      off();
      setStaleNotice(null);
    };
  }, [activeDraft?.serverId, activeDraft?.status, refreshAfterPosting]);

  // 86d3mnuxe — the outbox posting status for the open CL (the posting lifecycle moved off the entity).
  // Fetched on the active draft + refetched on an SSE posting nudge; drives the posting/failed card.
  const [posting, setPosting] = useState<OutboxPostingStatusDto | null>(null);
  useEffect(() => {
    const serverId = activeDraft?.serverId;
    if (!serverId) {
      setPosting(null);
      return;
    }
    let cancelled = false;
    const load = () =>
      fetchOutboxStatus(outboxKey.quotation(serverId))
        .then((jobs) => {
          // 86d3peur0 review — a re-post enqueues a NEW posting.cl job (jobs are ascending by id, so
          // jobs[0] is the OLDEST). Prefer an in-flight Posting job over a stale Failed sibling; else the
          // most-recent job.
          if (!cancelled)
            setPosting(jobs.find((j) => j.status === 'Posting') ?? jobs[jobs.length - 1] ?? null);
        })
        .catch(() => {
          if (!cancelled) setPosting(null);
        });
    void load();
    const off = onQuotationEvent((evt) => {
      if (evt.quotationId === serverId && evt.postingStatus != null) void load();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [activeDraft?.serverId]);

  // 86d3mnuxe funnel — resolve a Failed posting intent. Submitter-level (anyone who can post can
  // cancel their NotReached failure — the flow endpoint authorizes like the post; the BE refuses a
  // reached cancel). On success the job is Cancelled → excluded from the projection → null is truth.
  const canResolvePosting = useAuthStore(
    (s) =>
      s.hasPermission(PERMISSIONS.QUOTATIONS_POST_CUTTING_LIST) ||
      s.hasPermission(PERMISSIONS.SYSTEM_MANAGE_OUTBOX),
  );
  const handleCancelPost = useCallback(async () => {
    const serverId = activeDraft?.serverId;
    if (!serverId) return;
    try {
      await quotationService.cancelCuttingListPosting(serverId);
      setPosting(null);
      toast.success('Posting cancelled', {
        description: 'The failed post is closed — you can keep editing and submit again.',
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to cancel the post');
    }
  }, [activeDraft?.serverId]);

  // 86d3mnuxe funnel — Reconcile a REACHED failure: the BE re-arms the same job (frozen decision) so
  // the adopt records the Estimate NetSuite already accepted. No re-decision, no prompt.
  const handleReconcilePost = useCallback(async () => {
    const serverId = activeDraft?.serverId;
    if (!serverId) return;
    try {
      await quotationService.reconcileCuttingListPosting(serverId);
      markPosting(serverId);
      setPosting(optimisticPostingJob(outboxKey.quotation(serverId), outboxType.quotation));
      toast.info('Reconciling — recovering the record NetSuite already accepted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to start the reconcile');
    }
  }, [activeDraft?.serverId, markPosting]);

  const handleReloadStale = useCallback(async () => {
    const serverId = activeDraft?.serverId;
    if (!serverId) return;

    // Reload is a deliberate-overwrite signal. The loadDraft merge would
    // otherwise preserve unsaved header / freight edits even though the user
    // just asked for the server's version — and line-item edits would be
    // overwritten without warning (mirroring retry-extraction's existing
    // prompt at ReviewExtractionHeader.handleRetry). Confirm before
    // discarding, then bypass the merge via `force: true`.
    if (activeDraft?.dirty) {
      const confirmed = window.confirm(
        'Reloading will REPLACE your unsaved changes with the latest server version.\n\n' +
          'Any unsaved edits — including line-item changes — will be lost. Continue?',
      );
      if (!confirmed) return;
    }

    setIsReloadingStale(true);
    try {
      const fresh = await quotationService.getById(serverId);
      loadDraft(fresh, { force: true });
      setStaleNotice(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reload record');
    } finally {
      setIsReloadingStale(false);
    }
  }, [activeDraft?.dirty, activeDraft?.serverId, loadDraft]);

  const handleChange = useCallback(
    (draftId: string, patch: Partial<QuotationDraft>) => update(draftId, patch),
    [update],
  );

  const handleSaveDraft = useCallback(async () => {
    if (!activeDraft) return;
    const wasFirstSave = !activeDraft.serverId;
    // 86d32t157 Phase 3 — pass baseMarkup so the save includes the pricing-drift
    // baseline. baseMarkup is the panel-mount snapshot fetched from the intake
    // service; the store + service thread it through to buildPricingSnapshot.
    const saved = await saveDraft(activeDraft.id, baseMarkup);
    if (saved) {
      toast.success(wasFirstSave ? 'Draft saved' : 'Changes saved');
      // Also bump the parent so the master list refetches even on follow-up
      // saves (the row's UpdatedAt / status / age cells should refresh).
      if (saved.serverId) onAfterSave?.(saved.serverId);
    }
  }, [activeDraft, saveDraft, onAfterSave, baseMarkup]);

  // ── 86d32t0xr auto-save ──────────────────────────────────────────────────
  // Debounced trailing-edge save. Fires 2.5s after the last dirty=true
  // transition; intermediate dirty flips reset the timer (so 30 rapid edits
  // produce one save, not 30). Silent on failure — the status chip surfaces
  // errors. Manual Save still works and bypasses both the debounce and the
  // in-flight guard so the button feels responsive.
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // Always clear any pending debounce when dependencies change. The new
    // debounce is scheduled below only if all gates pass.
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    if (!activeDraft) return;
    if (!activeDraft.dirty) return;
    // Skip conditions match the manual-save flow (already enforced in the
    // store, but checking here avoids scheduling a no-op timer).
    if (activeDraft.status === QuotationStatus.Submitted) return;
    if (activeDraft.status === QuotationStatus.Sent) return;
    if (activeDraft.extractionStatus === 'processing') return;
    // 86d3mnuxe FIX 6 — never auto-save while the CL post is in flight or after a reached failure:
    // the BE mutation guard 400s the save, and a mid-post save would diverge from the frozen payload
    // NetSuite receives. Gate on `posting` DIRECTLY — `isReadOnly` is declared after the early
    // returns below and is not in scope here (TDZ). A NotReached failure keeps auto-save alive.
    if (postingLocksEntity(posting)) return;

    // Min-content gate (per ticket): only auto-save once the draft has at
    // least one of (a) a picked customer, (b) ≥1 line item, or (c) a prior
    // successful save (= serverId present). Stops phantom rows on idle "+ New"
    // clicks.
    const meetsMinContent =
      activeDraft.customerId != null ||
      activeDraft.lineItems.length > 0 ||
      activeDraft.serverId != null;
    if (!meetsMinContent) return;

    // Capture `wasFirstSave` BEFORE the timer fires. Auto-save owns the
    // first-save promotion to the workspace (mirroring handleSaveDraft), so
    // the IIFE below can fire onAfterSave with the freshly-assigned serverId.
    //
    // Using a primitive captured at schedule time (not `selectedId` read via
    // closure inside the IIFE) is deliberate: it avoids a stale-closure race
    // where the user navigates to another row during the debounce + save
    // round-trip and gets yanked back to the just-saved draft.
    const wasFirstSave = !activeDraft.serverId;
    autoSaveTimerRef.current = setTimeout(() => {
      // The store's saveDraft handles the in-flight guard internally when
      // called with `silent: true` (skips if another save is mid-flight; the
      // next dirty-flip schedules another debounce that will catch up).
      void (async () => {
        const saved = await saveDraft(activeDraft.id, baseMarkup, { silent: true });
        // Promote the workspace selection only on the actual local → server
        // transition. Subsequent saves of an already-tracked draft are no-ops
        // here; the in-flight guard above suppresses the toast/spinner; and if
        // the user navigated away during the debounce, we don't yank them
        // back.
        if (wasFirstSave && saved?.serverId) {
          onAfterSave?.(saved.serverId);
        }
      })();
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [activeDraft, baseMarkup, saveDraft, onAfterSave, posting]);

  const handleArchiveConfirm = useCallback(async () => {
    const id = activeDraft?.serverId ?? selectedId;
    if (!id) return;
    setIsArchiving(true);
    try {
      await quotationService.archive(id);
      toast.success('Quotation archived');
      setArchiveDialogOpen(false);
      onAfterSave?.(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to archive quotation');
    } finally {
      setIsArchiving(false);
    }
  }, [activeDraft, selectedId, onAfterSave]);

  const handleRestore = useCallback(async () => {
    const id = activeDraft?.serverId ?? selectedId;
    if (!id) return;
    setIsArchiving(true);
    try {
      await quotationService.restore(id);
      toast.success('Quotation restored');
      onAfterSave?.(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore quotation');
    } finally {
      setIsArchiving(false);
    }
  }, [activeDraft, selectedId, onAfterSave]);

  const handleDeleteConfirm = useCallback(async () => {
    const id = activeDraft?.serverId ?? selectedId;
    if (!id) return;
    setIsDeleting(true);
    try {
      await quotationService.deleteDraft(id);
      toast.success('Quotation deleted');
      setDeleteDialogOpen(false);
      onAfterSave?.(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete quotation');
    } finally {
      setIsDeleting(false);
    }
  }, [activeDraft, selectedId, onAfterSave]);

  const handleSubmit = useCallback(async () => {
    if (!activeDraft) return;

    // 86d3j5qca — pricing config must be loaded before we can price the post. A null
    // baseMarkup (still loading, or load failed) would post at cost — block with a
    // recoverable message. Per-grade no-markup lines (e.g. G33 / a zero base) are surfaced
    // in the review dialog's blocking bucket and enforced server-side.
    if (baseMarkup == null) {
      if (baseMarkupLoading) {
        toast.error('Pricing configuration is still loading — try again in a moment.');
      } else {
        toast.error('Couldn’t load pricing configuration.', {
          description: 'Base markup didn’t load, so pricing can’t be applied. Retry, then post again.',
          action: { label: 'Retry', onClick: () => void loadBaseMarkup() },
        });
      }
      return;
    }

    // CSC-307: hard pre-submit guard. The previous behaviour was to silently
    // drop unmatched / qty-zero / kg-unit lines from the POST in
    // quotationService.submitCuttingList — operators thought everything posted
    // but the NetSuite estimate was missing rows. Block the submit instead and
    // tell them exactly which rows are wrong, in one toast.
    let noMatch = 0;
    let noQty = 0;
    let noWeight = 0;
    for (const li of activeDraft.lineItems) {
      if (!isEditableRow(li)) continue;
      // 14ykpqtw3hw QUO-R-003e — a line still in kg never posts (isPostedRow), so it blocks submit as unmatched
      // even if it somehow carries a match; otherwise it would silently vanish from the Estimate.
      if (!li.matchedItemId || li.unit === 'kgs') noMatch++;
      if (li.quantity == null || li.quantity <= 0) noQty++;
      if ((li.weightKg ?? 0) <= 0) noWeight++;
    }
    if (noMatch > 0 || noQty > 0 || noWeight > 0) {
      const parts: string[] = [];
      if (noMatch > 0) parts.push(`${noMatch} unmatched`);
      if (noQty > 0) parts.push(`${noQty} with no qty`);
      if (noWeight > 0) parts.push(`${noWeight} with no weight`);
      toast.error(`Cannot create Cutting List: ${parts.join(', ')}.`);
      return;
    }

    // Save FIRST so the freight pre-check reads the server-validated state, not
    // the possibly-stale `freightCharge` memo from the render closure. The cabinet's
    // "₱X freight charge" comes from a local TS allocator render; the gate reads
    // server-canonical freightChargeJson. They can disagree when the autosave debounce
    // hasn't fired yet — and the gate would fire a false "freight is partially filled"
    // alert on a draft the cabinet shows as complete. Saving here forces the round-trip
    // and the store's merger now picks up freightChargeJson from the response so the
    // local memo and the saved snapshot agree.
    setIsSubmitting(true);
    let saved: QuotationDraft | null;
    try {
      saved = await saveDraft(activeDraft.id, baseMarkup);
    } catch (err) {
      setIsSubmitting(false);
      toast.error(err instanceof Error ? err.message : 'Could not save draft before submitting');
      return;
    }
    setIsSubmitting(false);
    if (!saved?.serverId) {
      toast.error('Could not save draft before submitting. Please try again.');
      return;
    }

    // Freight completeness note. A partially-filled freight cabinet (touched but no computed
    // total) means the Estimate would post WITHOUT a freight line — surface that as a SOFT
    // note in the confirmation modal (86d3hm2vr replaced the old window.confirm gate).
    // Suppress it entirely when no freight line is expected anyway: customer pick-up, freight
    // incorporated into price, or waived freight.
    const savedFreightInput = parseFreightInputDraft(saved.freightInputDraftJson);
    const savedFreightCharge = parseFreightCharge(saved.freightChargeJson);
    const noFreightExpected =
      saved.pickup === PickupType.Yes ||
      saved.pickup === PickupType.FreightIncorporated ||
      saved.waivedFreight === WaivedFreight.Yes;
    const fAny =
      savedFreightInput.freightDestinationId != null ||
      savedFreightInput.freightTruckType != null ||
      savedFreightInput.withinMetroManila;
    const fComplete =
      (savedFreightCharge?.kind === 'MetroManilaFlat' || savedFreightCharge?.kind === 'PerTonDopRate') &&
      savedFreightCharge.billedAmount > 0;

    // Stage the saved draft + freight note and open the review modal. The actual POST runs in
    // confirmPost (the dialog's onConfirm), also reused by the pricing-drift refresh path.
    setPendingPost({ saved, freightPartial: !noFreightExpected && fAny && !fComplete });
    setPostDialogOpen(true);
  }, [activeDraft, baseMarkup, baseMarkupLoading, loadBaseMarkup, saveDraft]);

  // The irreversible POST — runs from the confirmation modal's onConfirm (and the pricing-
  // drift refresh, after a fresh save). Creates a NetSuite Estimate; the row flips to
  // Submitted (read-only) with a permanent QD- number.
  const confirmPost = useCallback(
    async (saved: QuotationDraft, opts?: { acceptQuotedPricing?: boolean }) => {
      if (!saved.serverId) return;
      setIsSubmitting(true);
      try {
        // 86d3jkwuq — this is now a fast CLAIM (202). The worker posts to NetSuite off-thread;
        // optimistically flip to Posting so the spinner shows immediately, then the SSE echo
        // transitions to Submitted/PostingFailed (refreshed in place above). Also serves Retry
        // (re-claim from PostingFailed). Validation / no-postable-state errors surface here.
        await quotationService.submitCuttingList(saved, baseMarkup, opts);
        setPostDialogOpen(false);
        markPosting(saved.serverId);
        // 86d3peur0 #12 — show the posting card NOW. The claim has committed the job, but `posting` is only
        // refetched on the SSE nudge, so without this the panel sits in its pre-click state for a whole
        // round-trip. The refetch below replaces this echo with the server's row.
        setPosting(optimisticPostingJob(outboxKey.quotation(saved.serverId), outboxType.quotation));
        toast.info('Posting to NetSuite…', { description: `Customer: ${saved.customerName}` });
      } catch (err) {
        // 86d3mnuxe FIX 5 — drift is a synchronous 409 at the claim again: open the decision dialog
        // ([Keep quoted pricing] / [Use current pricing]) while the user is still here. pendingPost
        // is deliberately kept — the keep-quoted resubmit reuses the same saved draft.
        if (err instanceof PricingDriftError) {
          setPostDialogOpen(false);
          setPricingDriftDetail(err.detail);
          return;
        }
        // 14ykpqtr22u — show the server's reason (e.g. "DOP was updated while this page was open…"),
        // not axios's "Request failed with status code 400".
        const msg = getApiErrorMessage(err, 'Unable to start posting');
        toast.error(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [baseMarkup, markPosting],
  );

  // 86d3mnuxe FIX 5 — the [Keep quoted pricing] answer: resubmit the SAME saved draft with the
  // accept flag; the server posts at the snapshot pricing and records the audit (who/when/old→new).
  // No re-save — the snapshot being honored is exactly what's on the draft now.
  const handleKeepQuotedPricing = useCallback(async () => {
    const saved = pendingPost?.saved ?? activeDraft;
    if (!saved?.serverId) return;
    setPricingDriftDetail(null);
    await confirmPost(saved as QuotationDraft, { acceptQuotedPricing: true });
  }, [pendingPost, activeDraft, confirmPost]);

  // 86d3mnuxe funnel — NotReached Retry = cancel the failed intent, re-save (so the drift check
  // compares against what's on screen), then re-claim. A genuine re-decision: the "Pricing changed
  // during submission" dialog fires if the market moved since the failed attempt.
  const handleRetryFailedPost = useCallback(async () => {
    if (!activeDraft?.serverId) return;
    setIsSubmitting(true);
    try {
      await quotationService.cancelCuttingListPosting(activeDraft.serverId);
    } catch (err) {
      setIsSubmitting(false);
      toast.error(getApiErrorMessage(err, 'Unable to retry the post'));
      return;
    }
    let saved: QuotationDraft | null = null;
    try {
      saved = await saveDraft(activeDraft.id, baseMarkup);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the draft before retrying');
    } finally {
      setIsSubmitting(false);
    }
    if (saved) await confirmPost(saved);
    else setPosting(null); // old intent is cancelled; leave the draft editable for a manual submit
  }, [activeDraft, baseMarkup, saveDraft, confirmPost]);

  // 86d32t157 Phase 3 — Refresh-and-resubmit handler. The modal calls this when sales
  // acknowledges the drift; we close it, re-save (captures the now-current cache into
  // PricingSnapshotJson), then re-post directly — no need to re-open the review modal since
  // the user already confirmed. The re-post's drift check finds no diverge and proceeds.
  const handleDriftRefresh = useCallback(async () => {
    if (!activeDraft) return;
    setPricingDriftDetail(null);
    let saved: QuotationDraft | null;
    try {
      saved = await saveDraft(activeDraft.id, baseMarkup);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save before retrying');
      return;
    }
    if (!saved?.serverId) {
      toast.error('Could not save before retrying. Please try again.');
      return;
    }
    await confirmPost(saved);
  }, [activeDraft, baseMarkup, saveDraft, confirmPost]);

  // Total weight feeds the freight calculator. Defined BEFORE the early
  // returns so the hook order is stable across renders (Rules of Hooks).
  // Null-guarded internally — useMemo always runs, but returns 0 when there's
  // no active draft yet.
  const totalWeightKg = useMemo(() => {
    if (!activeDraft) return 0;
    const ctx = computePricingContext(activeDraft.lineItems);
    return ctx?.totalWeightKg ?? 0;
  }, [activeDraft]);

  // Drives the MM auto-truck-class rule on the freight panel: a 12m line forces a
  // trailer regardless of weight. Computed here from the draft's line items so the
  // freight cabinet doesn't need access to the full array.
  const hasTwelveMeterLengths = useMemo(() => {
    if (!activeDraft) return false;
    // 14ykpqtr22u — only rows that post, the same rule the server's freight recompute applies.
    return activeDraft.lineItems.some((li) => isPostedRow(li) && (li.lengthM ?? 0) >= 12);
  }, [activeDraft]);

  // 86d32r9rv — the cabinet edits a FreightInputDraft serialized into
  // `freightInputDraftJson`. We parse on render, pass the derived fields as
  // props to the cabinet, then merge any patch back into the JSON on change.
  const freightInput = useMemo(
    () => parseFreightInputDraft(activeDraft?.freightInputDraftJson),
    [activeDraft?.freightInputDraftJson],
  );

  // Server-validated FreightCharge (post-recompute on every save). Drives the
  // trigger-bar summary chip and the pre-submit completeness guard. Null while
  // inputs are incomplete or before the first save.
  const freightCharge = useMemo(
    () => parseFreightCharge(activeDraft?.freightChargeJson),
    [activeDraft?.freightChargeJson],
  );

  // The trigger bar truncates this summary, so it also travels as the bar's `title` (UI gate 6).
  const freightTriggerSummary =
    (freightCharge?.kind === 'MetroManilaFlat' || freightCharge?.kind === 'PerTonDopRate') &&
    freightCharge.billedAmount > 0
      ? `${formatPhp(freightCharge.billedAmount)} · ${freightCharge.numTrucks} × ` +
        `${freightCharge.truck === 'trailer' ? 'trailer' : '10-wheeler'}${freightCharge.numTrucks === 1 ? '' : 's'} ` +
        `to ${freightCharge.destination.cityName}`
      : null;

  // Same Rules-of-Hooks placement constraint as totalWeightKg above. The
  // closure no-ops when activeDraft hasn't loaded yet — the cabinet that
  // calls this isn't rendered until after the early returns clear, so the
  // null path is only there to keep the hook order stable, not because the
  // UI can actually hit it.
  const handleFreightChange = useCallback(
    (patch: FreightPatch) => {
      if (!activeDraft) return;
      const current = parseFreightInputDraft(activeDraft.freightInputDraftJson);
      const next = { ...current };
      if (patch.destinationId !== undefined) next.freightDestinationId = patch.destinationId;
      if (patch.withinMetroManila !== undefined) next.withinMetroManila = patch.withinMetroManila;
      if (patch.truckType !== undefined) next.freightTruckType = patch.truckType;
      if (patch.discount !== undefined) next.freightDiscount = patch.discount;
      if (patch.tollPerTruck !== undefined) next.freightTollPerTruck = patch.tollPerTruck;
      if (patch.trailerMinBillTons !== undefined) next.freightTrailerMinBillTons = patch.trailerMinBillTons;
      if (patch.maxLoadPerTruck !== undefined) next.freightMaxLoadPerTruck = patch.maxLoadPerTruck;
      // patch.total / patch.allocationSummary are server-computed echoes — we
      // do NOT persist them on the input draft. The validated FreightCharge
      // (freightChargeJson) is the source of truth for the total.
      next.waivedFreight = activeDraft.waivedFreight;
      const nextJson = serializeFreightInputDraft(next);
      if (nextJson !== activeDraft.freightInputDraftJson) {
        handleChange(activeDraft.id, { freightInputDraftJson: nextJson });
      }
    },
    [activeDraft, handleChange],
  );

  // Empty state shows only when there's nothing to compose: no row
  // selected AND no in-progress unsaved-local draft from the workspace's
  // `+ new draft` button.
  if (!selectedId && !activeDraft) {
    return <EmptyState />;
  }

  if (loadError) {
    return (
      <div className="p-4 text-sm text-destructive">
        Failed to load quotation: {loadError}
      </div>
    );
  }

  if (!activeDraft) {
    return <PanelSkeleton rows={8} className="m-4" />;
  }

  // 86d3mnuxe FIX 6 — the panel also locks while the CL post is in flight / after a reached failure
  // (mirrors the BE mutation guard; a NotReached failure stays editable — editing is the fix).
  const isReadOnly =
    activeDraft.status === QuotationStatus.Submitted ||
    activeDraft.status === QuotationStatus.Sent ||
    postingLocksEntity(posting);
  const isAutoDraft = activeDraft.status === QuotationStatus.AutoDraft;
  const isLoadingState = isLoadingDraft;

  // Mutual exclusion between the two cabinets — only one anchored overlay can
  // be visible at a time. Plain functions, not hooks — safe after the early
  // returns above.
  const openCustomerCabinet = () => {
    setCabinetOpen(true);
    setFreightCabinetOpen(false);
  };
  const openFreightCabinet = () => {
    setFreightCabinetOpen(true);
    setCabinetOpen(false);
  };

  // AutoDraft email-source metadata — folded into the top bar instead of
  // rendering AutoDraftReviewCard as a separate banner. The card duplicated
  // the actions already present in the bar (Save/Submit/Open) and added
  // padding/border that competed visually with the bar above it.
  const sender = selectedListItem?.sourceEmailFrom ?? null;
  const subject = selectedListItem?.sourceEmailSubject ?? null;
  const attachmentCount = selectedListItem?.sourceEmailAttachmentCount ?? 0;
  const gmailThreadId = selectedListItem?.sourceEmailGmailThreadId ?? null;
  const gmailUrl = isAutoDraft && gmailThreadId
    ? buildGmailUrl(gmailThreadId, mailboxAddress)
    : null;

  return (
    <div className="flex h-full flex-col">
      {/* Single consolidated header bar. Status + identifier on the left;
          for AutoDrafts we add a second line with the email subject + Gmail
          link + attachment count. Save/Submit/Open-in-compose on the right.
          The legacy AutoDraftReviewCard isn't rendered here — its actions
          (Start drafting / Review) are redundant inside the workspace where
          the draft is already loaded inline. */}
      <div className="flex items-start justify-between gap-3 border-b bg-background px-4 py-2">
        <div className="flex min-w-0 items-start gap-3">
          {/* Square icon chip sized to fit the header's content height
              (~h-12 to span the multi-line text block on AutoDraft rows).
              Same visual language as the master list. */}
          <QuotationStatusIcon
            status={activeDraft.status ?? QuotationStatus.Draft}
            className="h-12 w-12 rounded-md"
            iconClassName="h-6 w-6"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 truncate font-mono text-sm font-medium">
              <span className="truncate">{activeDraft.quotationNumber}</span>
              {/* CSC-291: extraction status mirrors the master-list badge so sales
                  can see at a glance whether the LLM run succeeded / failed without
                  scrolling back to the list. NotApplicable (manual) renders nothing. */}
              <DetailExtractionStatusBadge
                status={activeDraft.emailExtractionStatus}
                kgOnly={isKgOnlyDraft(activeDraft)}
              />
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {activeDraft.customerName || activeDraft.projectName || sender || '(no customer)'}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
              {/* Creator. Always rendered when we have a name on the wire — for AutoDrafts
                  this surfaces the system user that created the row, for manual drafts the
                  signed-in sales user. Kept visible across all states so submitted drafts
                  still expose audit info that the AutoDraft-specific chips below hide. */}
              {activeDraft.createdByName && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap"
                  title={`Created by ${activeDraft.createdByName}`}
                >
                  <UserRound className="h-3 w-3 shrink-0" />
                  <span className="truncate">Created by {activeDraft.createdByName}</span>
                </span>
              )}
              {isAutoDraft && subject && (
                <span className="inline-flex items-center gap-1 truncate" title={subject}>
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate">{subject}</span>
                </span>
              )}
              {attachmentCount > 0 && (
                <span
                  className="inline-flex items-center gap-0.5"
                  title={`${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}
                >
                  <Paperclip className="h-3 w-3" />
                  {attachmentCount}
                </span>
              )}
              {/* CSC-291: email-received age (AutoDraft only) and last-edited age
                  parallel the master-list display so the same freshness signals
                  carry through to the detail panel. */}
              {activeDraft.sourceEmail?.receivedAt && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap"
                  title={`Email received ${formatDateTime12h(activeDraft.sourceEmail.receivedAt)}`}
                >
                  <Mail className="h-3 w-3" />
                  {formatRelative(activeDraft.sourceEmail.receivedAt)}
                </span>
              )}
              {(activeDraft.updatedAt || activeDraft.createdAt) && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap"
                  title={`Last edited ${formatDateTime12h(activeDraft.updatedAt ?? activeDraft.createdAt)}`}
                >
                  <Pencil className="h-3 w-3" />
                  {formatRelative(activeDraft.updatedAt ?? activeDraft.createdAt)}
                </span>
              )}
              {gmailUrl && (
                <a
                  href={gmailUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open in Gmail
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Review toggle. AutoDrafts show their source email + extracted
              line items side-by-side; manual drafts show their uploaded
              attachments (CSC-283 supports multi-file manual uploads, so
              the source pane is meaningful for both). The review view
              renders an empty state when there's truly no source. */}
          <Button
            type="button"
            size="sm"
            variant={reviewMode ? 'secondary' : 'outline'}
            onClick={() => setReviewMode(!reviewMode)}
            title={reviewMode ? 'Back to compose' : 'Review source vs extracted lines'}
            aria-pressed={reviewMode}
          >
            {reviewMode
              ? <X className="mr-1.5 h-3.5 w-3.5" />
              : <Eye className="mr-1.5 h-3.5 w-3.5" />}
            {reviewMode ? 'Back' : 'Review'}
          </Button>
          {!isReadOnly && (
            <>
              {/* 86d32t0xr — auto-save status chip. Sits to the left of the
                  manual Save button so sales sees the state of the background
                  saver at the same glance as the manual override. Renders
                  nothing on Submitted/Sent (no auto-save runs there anyway). */}
              <AutoSaveStatusChip
                isSaving={isSaving}
                lastSavedAt={lastSavedAt}
                saveError={saveError}
                onRetry={handleSaveDraft}
                disabled={isSubmitting || isLoadingState}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={isSaving || isSubmitting || isLoadingState}
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSubmit}
                disabled={
                  isSaving || isSubmitting || isLoadingState || !currentUserHasNsEmployee ||
                  // 86d3mnuxe funnel — one posting intent at a time: while a job is in flight or
                  // Failed, submit is disabled and the posting card's Retry/Cancel/Reconcile is the
                  // way forward (the BE claim rejects it too).
                  postingBlocksSubmit(posting)
                }
                title={
                  !currentUserHasNsEmployee
                    ? 'Your account is not linked to a NetSuite employee. Contact an administrator to enable submission.'
                    : submitBlockedReason(posting) ?? 'Create a Cutting List in NetSuite from this draft'
                }
              >
                {isSubmitting ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                )}
                Create Cutting List
              </Button>
            </>
          )}
          {(() => {
            const isArchived = activeDraft?.isArchived ?? selectedListItem?.isArchived ?? false;
            const status = activeDraft?.status ?? selectedListItem?.status;
            const archivable = !isArchived
              && (status === QuotationStatus.Draft || status === QuotationStatus.AutoDraft);
            return (
              <>
                {canArchive && isArchived && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleRestore}
                    disabled={isArchiving}
                    title="Restore this quotation back to the active list"
                  >
                    {isArchiving
                      ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      : <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />}
                    Restore
                  </Button>
                )}
                {canArchive && archivable && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setArchiveDialogOpen(true)}
                    disabled={isArchiving || isSaving || isSubmitting}
                    title="Archive this draft (recoverable via the Archived tab)"
                  >
                    <Archive className="mr-1.5 h-3.5 w-3.5" />
                    Archive
                  </Button>
                )}
                {/* 86d315np2: Delete is only reachable from the archived state, so
                    operators have to consciously go through archive first. Soft-deletes
                    the row (IsDeleted=true) — recovery is DBA-only. */}
                {canDelete && isArchived && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isDeleting}
                    className="text-destructive hover:bg-destructive/10"
                    title="Delete this quotation"
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    Delete
                  </Button>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Cabinet-door trigger — full-width bar under the top header. Subtle
          by default, brightens on hover. Click toggles the overlay below.
          CSC-293: hidden in read-only mode (Submitted / Sent). The flat
          read-only summary inside QuotationComposeForm replaces the cabinet
          contents — there's nothing to edit, so there's nothing to hide. */}
      {!isReadOnly && (
        <button
          ref={cabinetTriggerRef}
          type="button"
          onClick={() => (cabinetOpen ? setCabinetOpen(false) : openCustomerCabinet())}
          aria-expanded={cabinetOpen}
          aria-controls="quotation-cabinet-overlay"
          title={cabinetOpen ? 'Hide customer details' : 'Show customer details'}
          className={cn(
            'flex w-full items-center justify-between gap-2 border-b px-4 py-2 text-sm transition-colors',
            cabinetOpen
              ? 'bg-muted text-foreground'
              : 'bg-muted/30 text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <span className="flex items-center gap-2">
            <UserRound className="h-4 w-4" />
            Customer Details
            <span className="text-xs opacity-70">
              {cabinetOpen ? '— click to hide' : '— click to expand'}
            </span>
          </span>
          {cabinetOpen
            ? <ChevronUp className="h-4 w-4" />
            : <ChevronDown className="h-4 w-4" />}
        </button>
      )}

      {/* Freight cabinet trigger — second cabinet, sits directly below the
          customer one. Editable drafts get the click-to-expand cabinet; Submitted /
          Sent quotations get a flat read-only summary at the same position (mirrors
          the Customer Details pattern — no edit affordance once the record is
          locked in). */}
      {FREIGHT_FEATURE_ENABLED && !isReadOnly && (
        <button
          ref={freightCabinetTriggerRef}
          type="button"
          onClick={() =>
            freightCabinetOpen ? setFreightCabinetOpen(false) : openFreightCabinet()
          }
          aria-expanded={freightCabinetOpen}
          aria-controls="quotation-freight-cabinet-overlay"
          title={freightCabinetOpen ? 'Hide freight calculator' : 'Show freight calculator'}
          className={cn(
            'flex w-full items-center justify-between gap-2 border-b px-4 py-2 text-sm transition-colors',
            freightCabinetOpen
              ? 'bg-muted text-foreground'
              : 'bg-muted/30 text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Truck className="h-4 w-4 shrink-0" />
            <span className="shrink-0">Freight & Delivery</span>
            {activeDraft.waivedFreight === '1' ? (
              <span className="truncate text-xs font-medium text-warning" title="Freight waived">
                · Freight waived
              </span>
            ) : freightTriggerSummary ? (
              <span className="truncate text-xs font-medium text-success" title={freightTriggerSummary}>
                · {freightTriggerSummary}
              </span>
            ) : (
              <span className="text-xs opacity-70">
                {freightCabinetOpen ? '— click to hide' : '— click to expand'}
              </span>
            )}
          </span>
          {freightCabinetOpen
            ? <ChevronUp className="h-4 w-4 shrink-0" />
            : <ChevronDown className="h-4 w-4 shrink-0" />}
        </button>
      )}


      {/* CSC-289: unread alert banner. Renders only when the master list has
          stamped this quotation as "first open" via the workspace store. The
          dismiss X clears the store flag; navigating to a read row clears it
          implicitly via handleRowOpen, so the banner doesn't linger when the
          user walks away from a row they just acknowledged. Custom div instead
          of shadcn Alert because Alert's CSS forces icon-left + stacked
          content, leaving lots of empty horizontal space. We want a single-row
          horizontal banner aligned with the body padding (mx-6 to match p-6
          below). Blue tint reads as informational rather than the brand red,
          which would imply error/warning.
          CSC-291: flat-band redesign — no horizontal margin, no rounded corners,
          border-y only so the alert reads as a continuation of the panel
          stack rather than a floating card. */}
      {activeDraft?.serverId != null && unreadAlertQuotationId === activeDraft.serverId && (
        <div
          role="status"
          className="flex items-center gap-3 border-y border-info/30 bg-info/10 px-4 py-2.5 text-sm"
        >
          {selectedListItem?.status === QuotationStatus.AutoDraft ? (
            <Mail className="h-4 w-4 shrink-0 text-info" />
          ) : (
            <Inbox className="h-4 w-4 shrink-0 text-info" />
          )}
          <div className="min-w-0 flex-1 truncate text-info">
            <span className="font-medium">
              {selectedListItem?.status === QuotationStatus.AutoDraft
                ? `New AutoDraft from ${parseSenderName(selectedListItem.sourceEmailFrom) || 'unknown sender'}`
                : `New draft: ${activeDraft.quotationNumber}`}
            </span>
            <span className="ml-2 text-info/80">
              {selectedListItem?.status === QuotationStatus.AutoDraft
                ? (selectedListItem.sourceEmailSubject ?? selectedListItem.projectName ?? activeDraft.customerName ?? '')
                : (activeDraft.customerName || activeDraft.projectName || '')}
            </span>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setUnreadAlertQuotationId(null)}
            className="shrink-0 rounded-sm p-1 text-info hover:bg-info/15 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-info"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Body region — flex column AND relative-positioned so (a) the form
          scroll container can flex-1 into a definite height (h-full inside
          a non-flex flex-1 ancestor doesn't always resolve cleanly in CSS,
          which left empty space below the sticky footer at certain zooms)
          and (b) the cabinet overlay can be absolutely positioned over the
          line items area. The overlay div is always mounted (portal target
          needs a stable DOM node); visibility flips via opacity so the
          customer card stays mounted in React's tree even when the cabinet
          is closed (preserves RHF input refs across open/close). */}
      <div className="relative flex flex-1 flex-col min-h-0 overflow-hidden">
        {/* `flex flex-1 flex-col` + `min-h-0` resolves a definite height via
            flex from the body region above. Without min-h-0, the default
            `min-height: auto` lets flex children grow to their content size
            and skip the flex-1 allocation, which left empty space below the
            sticky footer at non-100% zoom. `p-6` matches the legacy compose
            page so the sticky footer's `-mx-6 -mb-6` lands on this region's
            bottom edge instead of getting punched below it. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {/* 86d3mnuxe — posting-lifecycle card driven by the OUTBOX status (Posting spinner / Failed
              retry). null → no active posting job → renders nothing. 86d3peur0 review — once the CL is
              actually posted (Submitted/Sent) suppress any lingering stale Failed job from an earlier attempt
              (its successful successor is Done and drops off the projection). */}
          {posting &&
            activeDraft.status !== QuotationStatus.Submitted &&
            activeDraft.status !== QuotationStatus.Sent && (
            <CuttingListPostingCard
              posting={posting}
              onRetry={() => void handleRetryFailedPost()}
              onReconcile={() => void handleReconcilePost()}
              onCancel={canResolvePosting ? () => void handleCancelPost() : null}
              isBusy={isSubmitting}
            />
          )}
          {/* CSC-291: keep the compose form ALWAYS mounted so its customer-card
              portal-into-the-cabinet stays alive in review mode too. Visually swap
              between compose body and review view via `hidden`; the cabinet then
              works identically in both modes (without this, opening the cabinet in
              review mode showed a blank white box because the form had unmounted). */}
          <div className={cn(
            // CSC-291: zero padding so the body sits flush with the panel edges,
            // matching the header's flat, edge-to-edge layout. The action bar
            // inside QuotationComposeForm drops its negative margins to match.
            'flex min-h-0 flex-1 flex-col',
            reviewMode && 'hidden',
          )}>
            <QuotationComposeForm
              draft={activeDraft}
              isSubmitting={isSubmitting}
              baseMarkup={baseMarkup}
              readOnly={isReadOnly}
              onChange={handleChange}
              onSubmit={handleSubmit}
              customerPanelMode="portal"
              customerPanelPortalTarget={cabinetTargetEl}
            />
          </div>
          {/* 86d3fcbte — review goes full-bleed: the shell portals to <body> and
              covers the app sidebar + draft list. The compose form above stays
              mounted (just `hidden`) so its cabinet portal survives (CSC-291). */}
          {reviewMode && (
            <FullscreenReviewShell
              title={activeDraft.quotationNumber}
              subtitle={activeDraft.customerName || activeDraft.projectName || '(no customer)'}
              onExit={() => setReviewMode(false)}
            >
              <QuotationReviewView draft={activeDraft} />
            </FullscreenReviewShell>
          )}
        </div>
        {/* CSC-293: cabinet only renders in editable modes. Read-only
            quotations show a flat customer summary inside the form instead. */}
        {!isReadOnly && (
          <div
            id="quotation-cabinet-overlay"
            ref={(el) => setCabinetTargetEl(el)}
            // Dropdown-style overlay: full width, top-anchored, 80% of the
            // body height capped at 720px. The bottom strip of the body shows
            // the line items table — clicking there closes the cabinet
            // (caught by the mousedown listener above).
            className={cn(
              // CSC-293: z-40 keeps the cabinet above the sticky line-items
              // toolbar (z-30). shadcn popovers/dialogs sit at z-50 so they
              // still float above this overlay when needed.
              'absolute inset-x-0 top-0 z-40 h-[80%] max-h-[720px] overflow-y-auto border-b bg-background shadow-xl transition-opacity',
              cabinetOpen ? 'opacity-100' : 'pointer-events-none invisible opacity-0',
            )}
            aria-hidden={!cabinetOpen}
          />
        )}

        {/* Freight cabinet overlay. Same dimensions/z-index as the customer
            cabinet — they're mutually exclusive (see openCustomerCabinet /
            openFreightCabinet). FreightCalculatorPanel reads its state from
            the draft so selections persist across reloads via the existing
            Save flow. */}
        {FREIGHT_FEATURE_ENABLED && !isReadOnly && (
          <div
            id="quotation-freight-cabinet-overlay"
            ref={freightCabinetRef}
            className={cn(
              'absolute inset-x-0 top-0 z-40 h-[80%] max-h-[720px] overflow-y-auto border-b bg-background shadow-xl transition-opacity',
              freightCabinetOpen ? 'opacity-100' : 'pointer-events-none invisible opacity-0',
            )}
            aria-hidden={!freightCabinetOpen}
          >
            <FreightCalculatorPanel
              totalWeightKg={totalWeightKg}
              hasTwelveMeterLengths={hasTwelveMeterLengths}
              destinationId={freightInput.freightDestinationId}
              withinMetroManila={freightInput.withinMetroManila}
              truckType={freightInput.freightTruckType}
              discount={freightInput.freightDiscount}
              tollPerTruck={freightInput.freightTollPerTruck}
              trailerMinBillTons={freightInput.freightTrailerMinBillTons}
              maxLoadPerTruck={freightInput.freightMaxLoadPerTruck}
              waivedFreight={activeDraft.waivedFreight}
              readOnly={isReadOnly}
              onChange={handleFreightChange}
            />
          </div>
        )}

        {/* Stale-record blocking modal. Sits above the cabinet (z-50) and
            covers the whole body region so the user cannot keep editing
            into a stale state. The header stays interactive so they can
            navigate away if they prefer. */}
        {staleNotice && (
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="quotation-stale-title"
            className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 p-6 backdrop-blur-sm"
          >
            <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                <div className="min-w-0 flex-1">
                  <h3 id="quotation-stale-title" className="text-base font-semibold">
                    {staleNotice.kind === 'deleted'
                      ? 'This draft was deleted'
                      : staleNotice.status === QuotationStatus.Submitted
                        ? 'This draft was just submitted'
                        : 'This record was updated'}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {staleNotice.kind === 'deleted'
                      ? 'Another user deleted this record. The view you are currently seeing is no longer available.'
                      : 'Another user changed this record. Reload to see the latest state — your unsaved local edits will be replaced.'}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                {staleNotice.kind === 'deleted' ? (
                  <Button autoFocus onClick={() => setStaleNotice(null)}>
                    Close
                  </Button>
                ) : (
                  <Button autoFocus onClick={handleReloadStale} disabled={isReloadingStale}>
                    {isReloadingStale ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Reloading…
                      </>
                    ) : (
                      'Reload'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <ConfirmDraftActionDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Archive draft"
        description={`Archive ${activeDraft?.quotationNumber ?? selectedListItem?.quotationNumber ?? 'this draft'}? It will be hidden from active views but recoverable.`}
        confirmLabel="Archive"
        onConfirm={handleArchiveConfirm}
        isLoading={isArchiving}
      />
      <ConfirmDraftActionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete draft"
        description={`Delete ${activeDraft?.quotationNumber ?? selectedListItem?.quotationNumber ?? 'this draft'}? Archived drafts will be removed.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
      {pendingPost && (
        <CuttingListPostDialog
          open={postDialogOpen}
          onOpenChange={setPostDialogOpen}
          draft={pendingPost.saved}
          baseMarkup={baseMarkup}
          freightPartial={pendingPost.freightPartial}
          isSubmitting={isSubmitting}
          onConfirm={() => void confirmPost(pendingPost.saved)}
        />
      )}
      {pricingDriftDetail && (
        <PricingDriftDialog
          open={pricingDriftDetail !== null}
          detail={pricingDriftDetail}
          onClose={() => setPricingDriftDetail(null)}
          onRefreshAndResubmit={handleDriftRefresh}
          // 86d3mnuxe FIX 5 — CL-only: honor the quoted (snapshot) pricing, audited server-side.
          onKeepQuoted={handleKeepQuotedPricing}
          // Show the matched SKU description per drifted line, not the raw NetSuite item id.
          resolveItemLabel={(id) =>
            activeDraft.lineItems.find((li) => li.matchedItemId === id)?.matchedSalesDescription ?? undefined
          }
        />
      )}
    </div>
  );
}

function buildGmailUrl(threadId: string, mailboxAddress: string | null): string {
  return mailboxAddress
    ? `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(mailboxAddress)}#inbox/${threadId}`
    : `https://mail.google.com/mail/u/0/#inbox/${threadId}`;
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
      <Inbox className="h-10 w-10 opacity-30" />
      <div className="text-sm">Select a quotation to start composing</div>
      <div className="text-xs opacity-75">
        <kbd className="rounded border bg-muted px-1 text-[10px]">j</kbd> /{' '}
        <kbd className="rounded border bg-muted px-1 text-[10px]">k</kbd> to cycle the list,{' '}
        <kbd className="rounded border bg-muted px-1 text-[10px]">/</kbd> to search,{' '}
        <kbd className="rounded border bg-muted px-1 text-[10px]">p</kbd> to pin.
      </div>
    </div>
  );
}

